import React, { useEffect, useRef, useState } from "react";
import { IActionHandler, OrgInfo, EmailGroup, MessageDisplayType, AdminUserBase, Sort } from "@preveil-api";
import {
  Account, AdminMessages, AdminErrorMessages, AdminSuccessMessages, GlobalErrorMessages, collectionApi, useAppDispatch, useAppSelector, useGetOrgInfo, usePostUsersOrgsByEntityIdRecipientGroupsMutation,
  useDeleteUsersOrgsByEntityIdRecipientGroupsAndAliasMutation, usePutUsersOrgsByEntityIdRecipientGroupsAndAliasMutation, useGetUsersOrgsByEntityIdRecipientGroupsQuery, Message, MessageAnchors, MessageHandlerDisplayType, MessageToastTypes, isSameUser, GetUsersOrgsByEntityIdRecipientGroupsApiResponse, GetUsersOrgsByEntityIdRecipientGroupsAndAliasApiResponse,
  sortStrings, getOppositeSortDirection, SORT_DIRECTION, UserResultsMode
} from "src/common";
import { PageHeader, CoverTemplate } from "src/components";
import { CreateEmailGroupModal, Toolbar, ListView, SidePanel } from ".";
import { adminActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  org_info: OrgInfo;
}

// Description: EmailGroupComponent is a React component that manages user groups for an organization.
// It allows users to create, update, delete, and view user groups, as well as manage group members.
// An Email Group is a collection of email addresses with an alias that can be used to send emails to multiple recipients at once.
function EmailGroupComponent(props: AllProps) {
  const { current_account, org_info } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const [aliases, setAliases] = useState<string[]>([]);
  const [edit_mode, setEditMode] = useState<boolean>(false);
  const [show_modal, setShowModal] = useState<boolean>(false);
  const [is_loading, setIsLoading] = useState<boolean>(true);
  const [show_copy_modal, setShowCopyModal] = useState<EmailGroup>();
  const [sort, setSort] = useState<Sort<string>>({ field: "address", direction: "asc" });
  const [org_users, setOrgUsers] = useState<AdminUserBase[]>([]);
  const [selected_email_group, setSelectedEmailGroup] = useState<EmailGroup>();
  const [is_loading_group_members, setIsLoadingGroupMembers] = useState<boolean>(true);
  const pending_group_lookup = useRef<{ alias: string, members: string[], copy?: true }>();
  const generation_ref = useRef<number>(0);
  const [postUsersOrgsByEntityIdRecipientGroups] = usePostUsersOrgsByEntityIdRecipientGroupsMutation();
  const [putUsersOrgsByEntityIdRecipientGroupsAndAlias] = usePutUsersOrgsByEntityIdRecipientGroupsAndAliasMutation();
  const [deleteUsersOrgsByEntityIdRecipientGroupsAndAlias] = useDeleteUsersOrgsByEntityIdRecipientGroupsAndAliasMutation();
  const [getUsersOrgsByEntityIdRecipientGroupsAndAlias] = collectionApi.endpoints.getUsersOrgsByEntityIdRecipientGroupsAndAlias.useLazyQuery();
  const [getUsersOrgsByEntityIdRecipientGroups] = collectionApi.endpoints.getUsersOrgsByEntityIdRecipientGroups.useLazyQuery();

  const { data, error } = useGetUsersOrgsByEntityIdRecipientGroupsQuery({
    account_ids: Account.getAccountIdentifiers(current_account),
    body: {
      entityId: org_info.org_id
    }
  });
  const { data: org_data, error: org_error, refresh } = useGetOrgInfo(current_account, org_info, UserResultsMode.lightweight);

  // Description: Fetch org info on mount.
  useEffect(() => {
    refresh();
  }, []);

  // Description: Reset selected groups when component unmounts.
  useEffect(() => {
    return () => {
      dispatch(adminActions.setSelected([]));
    };
  }, []);

  // Description: Fetches the user groups and sorts them.
  useEffect(() => {
    if (!!data) {
      const sorted = sortStrings(data.aliases, { field: sort.field, direction: sort.direction });
      setAliases(sorted);
      setIsLoading(false);
    } else if (!!error) {
      const params = {
        message: AdminErrorMessages.error_fetching_admin_groups,
        stack: error
      };
      dispatchPageError(params);
      setIsLoading(false);
    }
  }, [data, error]);

  // Description: Fetches the org users and sets them to state.
  useEffect(() => {
    if (!!org_data) {
      setOrgUsers(org_data.lightweight_org_users);
      setIsLoadingGroupMembers(false);
    }

    if (org_error) {
      const params = {
        message: AdminErrorMessages.error_fetching_org_info,
        stack: org_error
      };
      dispatchPageError(params);
      setIsLoadingGroupMembers(false);
    }
  }, [org_data, org_error]);

  // Description: Retries resolving a group's members once org_users finishes loading, for a lookup that ran while it was still empty
  useEffect(() => {
    if (!!pending_group_lookup.current && org_users.length > 0) {
      const { alias, members, copy } = pending_group_lookup.current;
      pending_group_lookup.current = undefined;
      const resolved_members = resolveGroupMembers(members);
      setIsLoadingGroupMembers(false);
      if (copy) {
        setShowCopyModal({ alias, members: resolved_members });
        setShowModal(true);
      } else {
        setSelectedEmailGroup({ alias, members: resolved_members });
      }
    }
  }, [org_users]);

  // Description: Updates the admin group:
  async function updateAdminGroup(params: { alias: string, add: string[], remove: string[] }) {
    const account_ids = Account.getAccountIdentifiers(current_account);
    const { alias, add, remove } = params;
    const body = {
      add,
      remove,
      alias,
      entityId: org_info.org_id
    };
    await putUsersOrgsByEntityIdRecipientGroupsAndAlias({
      account_ids,
      body
    })
      .unwrap()
      .then(() => {
        const message = AdminSuccessMessages.success_update_admin_group.replace(MessageAnchors.user_id, alias);
        getGroupMembers(alias);
        dispatch(uiActions.handleSetMessage(new Message(message)));
      })
      .catch((error) => {
        const params = {
          message: AdminErrorMessages.error_updating_admin_group.replace(MessageAnchors.user_id, alias),
          stack: error
        };
        dispatchPageError(params);
      });
  }

  // Description: refreshes the email group list
  async function refreshAdminGroupList() {
    setIsLoading(true);
    const body = { entityId: org_info?.org_id };
    await getUsersOrgsByEntityIdRecipientGroups({
      account_ids: Account.getAccountIdentifiers(current_account),
      body
    }).unwrap()
      .then((result: GetUsersOrgsByEntityIdRecipientGroupsApiResponse) => {
        const { aliases } = result;
        setAliases(aliases);
        setIsLoading(false);
      })
      .catch((error: any) => {
        const params = {
          message: AdminErrorMessages.error_fetching_admin_groups,
          stack: error
        };
        dispatchPageError(params);
      });
  }

  // Description: Resolves raw member ids to AdminUserBase objects against the currently loaded org_users
  function resolveGroupMembers(members: string[]): AdminUserBase[] {
    const selected_group_members: AdminUserBase[] = [];
    members.forEach((member: string) => {
      const user = org_users?.find((user: AdminUserBase) => isSameUser(user.user_id, member));
      !!user && selected_group_members.push(user);
    });
    return selected_group_members;
  }

  // Description: gets the group members of a specific group_alias.
  async function getGroupMembers(group_alias: string, copy?: true) {
    setIsLoadingGroupMembers(true);
    if (!!group_alias) {
      const generation = ++generation_ref.current;
      const body = {
        entityId: org_info.org_id,
        alias: group_alias,
      };
      await getUsersOrgsByEntityIdRecipientGroupsAndAlias({
        account_ids: Account.getAccountIdentifiers(current_account),
        body,
      })
        .unwrap()
        .then((result: GetUsersOrgsByEntityIdRecipientGroupsAndAliasApiResponse) => {
          if (generation_ref.current !== generation) return;
          const { members } = result;
          if (members) {
            if (org_users.length === 0) {
              // NOTE: org_users hasn't loaded yet -- defer resolution so we don't show an incorrectly-empty member list
              pending_group_lookup.current = { alias: group_alias, members, copy };
              return;
            }
            const resolved_members = resolveGroupMembers(members);
            setIsLoadingGroupMembers(false);
            if (copy) {
              setShowCopyModal({ alias: group_alias, members: resolved_members });
              setShowModal(!show_modal);
            } else {
              setSelectedEmailGroup({ alias: group_alias, members: resolved_members });
            }
          }
        });
    }
  }

  // Description: Request when user is creating a new group.
  async function createNewAdminGroup(alias: string, members: string[]) {
    const account_ids = Account.getAccountIdentifiers(current_account);
    const body = {
      entityId: org_info.org_id,
      alias,
      members
    };
    await postUsersOrgsByEntityIdRecipientGroups({ account_ids, body })
      .unwrap()
      .then(() => {
        const message = AdminSuccessMessages.success_create_admin_group.replace(MessageAnchors.user_id, `<b>${alias}</b>`);
        dispatch(uiActions.handleSetMessage(new Message(message)));
        EmailGroupRequests.handleShowModal();
        refreshAdminGroupList();
      });
  }

  // Description: Delete Groups/Aliases.
  async function removeGroupFromList(group_aliases: string[]) {
    setIsLoading(true);
    if (!!group_aliases) {
      const entity_id = org_info.org_id;
      const account_ids = Account.getAccountIdentifiers(current_account);
      await Promise.all(group_aliases.map((alias: string) => {
        return deleteUsersOrgsByEntityIdRecipientGroupsAndAlias({ account_ids, body: { entityId: entity_id, alias } })
          .unwrap()
          .then(() => {
            const message = AdminSuccessMessages.success_delete_admin_group.replace(MessageAnchors.message_content, `${group_aliases.length.toString()} selected user group${group_aliases.length > 1 ? "s" : ""}`);
            dispatch(uiActions.handleSetMessage(new Message(message)));
            dispatch(adminActions.setSelected([]));
            setIsLoading(false);
          })
          .catch((error) => {
            const params = {
              message: AdminErrorMessages.error_remove_admin_group_aliases,
              stack: error
            };
            dispatchPageError(params);
            setIsLoading(false);
          });
      }));
    }
  }

  // Description: Confirmation modal for deleting user group emails
  function confirmDeleteGroupDialog(group_aliases: string[]) {
    const confirmation_dialog = new Message(
      AdminMessages.confirm_delete_group.body,
      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      AdminMessages.confirm_delete_group.title,
      {
        label: "Yes",
        data: true,
        action: () => removeGroupFromList(group_aliases)
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  const EmailGroupRequests = {
    handleRefresh: () => {
      refreshAdminGroupList();
    },
    handleSelect: (params: { id?: string, selected: boolean; }) => {
      let new_selected = selected.slice();
      const _id = params.id;
      if (!!_id) { // Note: individual check
        if (params.selected) {
          !_.find(new_selected, (_selected: string) => (_selected === _id)) &&
            new_selected.push(_id);
        } else {
          const index = _.findIndex(new_selected, (_selected: string) => (_selected === _id));
          index >= 0 && (new_selected.splice(index, 1));
        }
      } else { // Note: bulk actions
        new_selected = params.selected ? aliases : [];
      }
      dispatch(adminActions.setSelected(new_selected));
    },
    handleSetSort: (params: "address") => {
      const direction = sort.field === params ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending;
      const sorted_requests = sortStrings(aliases, { field: params, direction });
      setSort({ field: params, direction });
      setAliases(sorted_requests);
    },
    handleShowSideNavMenu: (params?: { group_name: string }) => {
      !!params ? getGroupMembers(params.group_name) : setSelectedEmailGroup(undefined);
    },
    handleReset: () => {
      // User click deselect all in side-nav panel.
      setSelectedEmailGroup(undefined);
      dispatch(adminActions.setSelected([]));
    },
    handleShowModal: () => {
      setEditMode(false);
      setShowCopyModal(undefined);
      setSelectedEmailGroup(undefined);
      setShowModal(!show_modal);
    },
    handleEdit: (params: { selected_group: EmailGroup }) => {
      setEditMode(true);
      setShowModal(true);
      setSelectedEmailGroup(params.selected_group);
    },
    handleCopy: () => {
      const selected_group = selected[0];
      !!selected_group && getGroupMembers(selected_group, true);
    },
    handleCreate: (params: { alias: string, members: string[] }) => {
      const { alias, members } = params;
      createNewAdminGroup(alias, members);
    },
    handleDelete: () => {
      const group_aliases = selected.length > 0 ? selected : undefined;
      !!group_aliases && confirmDeleteGroupDialog(group_aliases);
    },
    handleUpdate: (params: { alias: string, add: string[], remove: string[] }) => {
      updateAdminGroup(params);
    },
    handlePageErrorMessage: (params: { message: string, stack?: any, type?: MessageDisplayType }) => {
      const { message, stack, type } = params;
      const _type = type || MessageHandlerDisplayType.logger;
      dispatch(uiActions.handleRequestErrors(new Message(message, _type), stack));
    }
  };

  // Description: dispatch any error found in the page.
  function dispatchPageError(params: { message: string, stack?: any }) {
    dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.toastr), params.stack));
  }

  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((EmailGroupRequests as any)[callback] instanceof Function) {
      (EmailGroupRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      EmailGroupRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  return <CoverTemplate className="admin-component">
    <PageHeader>
      <h1>User Groups</h1>
      <Toolbar is_loading={is_loading} groups_aliases={aliases} handleAction={handlePageActions} />
    </PageHeader>
    <div id="outer-scope" className="cover-content">
      <ListView groups_aliases={aliases} is_loading={is_loading} selected={selected} sort={sort} handleAction={handlePageActions} />
    </div>
    {!!selected_email_group && <SidePanel is_loading={is_loading_group_members} handleAction={handlePageActions} selected_group={selected_email_group}></SidePanel>}
    <CreateEmailGroupModal
      show_modal={show_modal}
      handleAction={handlePageActions}
      org_users={org_users}
      aliases={aliases}
      current_account={current_account}
      selected_group={show_copy_modal || selected_email_group}
      is_update_mode={edit_mode}
    />
  </CoverTemplate >;
}

export default React.memo(EmailGroupComponent);
